import { useCallback } from 'react';

/**
 * useFieldActions - Custom hook for field actions (clear, delete, click)
 * @param {boolean} isExtrationOptionShown - Whether extraction option is shown
 * @param {Function} onSelectExtract - Select extract handler
 * @param {Function} onEnableValue - Enable value handler
 * @param {Function} onSelectColumn - Select column handler
 * @param {Function} onClearFundValues - Clear fund values handler
 * @param {Function} handleDeleteCustomField - Delete custom field handler
 * @param {Function} setAction - Set action handler for modal
 * @param {Object} selectedColumn - Currently selected column
 * @returns {Object} Field action handlers
 */
export const useFieldActions = (
  isExtrationOptionShown,
  onSelectExtract,
  onEnableValue,
  onSelectColumn,
  onClearFundValues,
  handleDeleteCustomField,
  setAction,
  selectedColumn
) => {
  const handleFieldClick = useCallback(
    (item, table, selectedItem) => () => {
      if (isExtrationOptionShown) {
        onSelectExtract({
          ...item,
          table: table.name
        });
      } else {
        onEnableValue(table?.id, selectedItem ? null : item);
      }
    },
    [isExtrationOptionShown, onSelectExtract, onEnableValue]
  );

  const handleRequiredFieldClick = useCallback(
    item => () => {
      if (isExtrationOptionShown) {
        onSelectExtract(item);
      } else {
        onSelectColumn(selectedColumn?.id === item.id ? null : item);
      }
    },
    [isExtrationOptionShown, onSelectExtract, onSelectColumn, selectedColumn]
  );

  const handleCustomFieldClick = useCallback(
    item => () => {
      if (isExtrationOptionShown) {
        onSelectExtract(item);
      } else {
        onSelectColumn(selectedColumn?.id === item.id ? null : item);
      }
    },
    [isExtrationOptionShown, onSelectExtract, onSelectColumn, selectedColumn]
  );

  const handleClearColumn = useCallback(
    item => () => {
      setAction({
        title: 'Delete column data',
        description: 'Are you sure you want to delete the data in this column?',
        colorSchema: 'red',
        confirmButtonTitle: 'Yes, Delete Data',
        onConfirm: () => {
          onClearFundValues(item.id);
          setAction({});
          if (isExtrationOptionShown) onSelectExtract(item);
          else onSelectColumn(item);
        }
      });
    },
    [onClearFundValues, setAction, isExtrationOptionShown, onSelectExtract, onSelectColumn]
  );

  const handleDeleteField = useCallback(
    field => {
      handleDeleteCustomField(field.uuid || field.id);
    },
    [handleDeleteCustomField]
  );

  return {
    handleFieldClick,
    handleRequiredFieldClick,
    handleCustomFieldClick,
    handleClearColumn,
    handleDeleteField
  };
};
